import type {
  RorosWorkItemDTO,
  RorosWorkItemHistoryDTO,
  RorosWorkItemLinkDTO,
  RorosWipStatusDTO,
  CreateWorkItemPayload,
  UpdateWorkItemPayload,
  TransitionWorkItemPayload,
  CreateLinkPayload,
  VerificationInboxResponse,
  WorkItemIterationsDTO,
} from '@/types/roros/control-tower';
import type {
  RorosSavedViewDTO,
  CreateSavedViewPayload,
  UpdateSavedViewPayload,
  SavedViewScope,
} from '@/types/roros/saved-views';
import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Control Tower ──────────────────────────────────────────────────────────

export const RorosControlTowerService = {
  listWorkItems: (params?: Record<string, string>) =>
    fetchJson<RorosWorkItemDTO[]>(`/api/v1/control-tower/work-items${qs(params)}`),
  createWorkItem: (payload: CreateWorkItemPayload) =>
    mutate<RorosWorkItemDTO>('/api/v1/control-tower/work-items', 'POST', payload),
  getWorkItem: (id: string) =>
    fetchJson<RorosWorkItemDTO>(`/api/v1/control-tower/work-items/${id}`),
  updateWorkItem: (id: string, payload: UpdateWorkItemPayload) =>
    mutate<RorosWorkItemDTO>(`/api/v1/control-tower/work-items/${id}`, 'PUT', payload),
  transitionWorkItem: (id: string, payload: TransitionWorkItemPayload) =>
    mutate<RorosWorkItemDTO>(`/api/v1/control-tower/work-items/${id}/transition`, 'POST', payload),
  hardDeleteWorkItem: (id: string) =>
    mutate<{ deleted: boolean; deletedCount: number }>(
      `/api/v1/control-tower/work-items/${id}/hard-delete`,
      'POST',
    ),
  createLink: (id: string, payload: CreateLinkPayload) =>
    mutate<RorosWorkItemLinkDTO>(`/api/v1/control-tower/work-items/${id}/links`, 'POST', payload),
  deleteLink: (id: string, linkId: string) =>
    mutate(`/api/v1/control-tower/work-items/${id}/links/${linkId}`, 'DELETE'),
  getHistory: (id: string) =>
    fetchJson<RorosWorkItemHistoryDTO[]>(`/api/v1/control-tower/work-items/${id}/history`),
  getWipStatus: () =>
    fetchJson<RorosWipStatusDTO>('/api/v1/control-tower/wip-status'),
  getOverview: () =>
    fetchJson<Record<string, unknown>>('/api/v1/control-tower/overview'),
  getVerificationInbox: () =>
    fetchJson<VerificationInboxResponse>('/api/v1/control-tower/verification-inbox'),

  // ─── J7-W5 task 6c-1: Sub-items / parent-child rollup ─────────────────────
  getWorkItemTree: (id: string) =>
    fetchJson<RorosWorkItemDTO[]>(`/api/v1/control-tower/work-items/${id}/tree`),
  getRollupStats: (id: string) =>
    fetchJson<{
      total: number;
      completed: number;
      blocked: number;
      escalated: number;
      byStatus: Record<string, number>;
    }>(`/api/v1/control-tower/work-items/${id}/rollup`),

  // ─── v1.1.20: Iteration memory (deliverable versions + paired QC reports) ─
  getIterations: (id: string) =>
    fetchJson<WorkItemIterationsDTO>(`/api/v1/control-tower/work-items/${id}/iterations`),
};

// ─── J7-W5 task 6c-2: Saved Views ──────────────────────────────────────────

export const RorosSavedViewService = {
  list: (scope: SavedViewScope = 'control_tower') =>
    fetchJson<RorosSavedViewDTO[]>(`/api/v1/saved-views${qs({ scope })}`),
  create: (payload: CreateSavedViewPayload) =>
    mutate<RorosSavedViewDTO>('/api/v1/saved-views', 'POST', payload),
  update: (id: string, payload: UpdateSavedViewPayload) =>
    mutate<RorosSavedViewDTO>(`/api/v1/saved-views/${id}`, 'PUT', payload),
  remove: (id: string) =>
    mutate(`/api/v1/saved-views/${id}`, 'DELETE'),
};

// ─── Observability ──────────────────────────────────────────────────────────

/**
 * Matches the server `HealthSignal` shape from
 * server/modules/observability/observability.types.ts
 *
 * `value` is `number | Record<string, number> | null`:
 *  - Most signals are scalar numbers
 *  - Distribution signals (maturity, decision_method_dist, etc.) return an object
 *  - `null` means insufficient data
 */
export interface RorosHealthSignalDTO {
  key: string;
  label: string;
  value: number | Record<string, number> | null;
  unit: string;
  status: 'ok' | 'warning' | 'critical' | 'informational';
  threshold: { warning: number; critical: number; direction: 'above' | 'below' } | null;
}

export interface RorosTrendSignalDTO extends RorosHealthSignalDTO {
  trend: { current: number | null; previous: number | null; changePct: number | null } | null;
}

export interface RorosMuraIndicatorDTO {
  signal: string;
  mean: number;
  stddev: number;
  coefficientOfVariation: number;
  status: 'ok' | 'warning' | 'critical' | 'informational';
  weeklyValues: number[];
}

export interface RorosMuraDTO {
  fttMura: RorosMuraIndicatorDTO | null;
  cycleTimeMura: RorosMuraIndicatorDTO | null;
  overallStatus: 'ok' | 'warning' | 'critical' | 'informational';
}

// Legacy aliases — kept for backward compat with existing call sites that
// used the loose `Record<string, unknown>` typing.
export type RorosHealthDTO = RorosHealthSignalDTO[];
export type RorosScorecardDTO = RorosHealthSignalDTO[];
export type RorosTrendDTO = RorosTrendSignalDTO;

/**
 * Server response shape for `/api/v1/observability/scorecard/{ceo,domain/:id}`.
 *
 * Server returns a `ScorecardView` object: `{ signals: [...], scope, generatedAt, ...}`.
 * Most callers (ScorecardPage etc.) just want the signals array, so the service
 * unwraps `.signals` below. Anyone needing scope/generatedAt should use
 * `getCeoScorecardView` instead.
 */
export interface RorosScorecardViewDTO {
  signals: RorosHealthSignalDTO[];
  scope: 'system' | 'domain';
  domainId?: string;
  generatedAt: string;
}

export const RorosObservabilityService = {
  getHealth: () =>
    fetchJson<RorosHealthSignalDTO[]>('/api/v1/observability/health'),
  getDomainHealth: (domainId: string) =>
    fetchJson<RorosHealthSignalDTO[]>(`/api/v1/observability/health/${domainId}`),
  // J6 fix-wave 1: server returns a ScorecardView object, not a bare signals
  // array. Unwrap `.signals` so existing callers keep their array contract
  // (ScorecardPage.tsx:100 does `signals.find(...)`). Without this unwrap the
  // page throws "signals?.find is not a function" because data is the object.
  getCeoScorecard: async (): Promise<RorosHealthSignalDTO[]> => {
    const view = await fetchJson<RorosScorecardViewDTO>('/api/v1/observability/scorecard/ceo');
    return view?.signals ?? [];
  },
  getDomainScorecard: async (domainId: string): Promise<RorosHealthSignalDTO[]> => {
    const view = await fetchJson<RorosScorecardViewDTO>(`/api/v1/observability/scorecard/domain/${domainId}`);
    return view?.signals ?? [];
  },
  getTrends: (params?: Record<string, string>) =>
    fetchJson<RorosTrendSignalDTO[]>(`/api/v1/observability/trends${qs(params)}`),
  getMura: () =>
    fetchJson<RorosMuraDTO>('/api/v1/observability/mura'),

  /**
   * J6 — Build the URL the browser should hit for a Road-dashboard download.
   * The endpoint sets Content-Disposition: attachment, so the browser will
   * trigger a file save when navigating to it.
   *
   * NOTE: returns a URL string rather than fetching directly because the
   * browser must own the download (cookie/auth headers go via fetch wrapper
   * for navigation? — Wrong. We use apiFetch + Blob in the page component so
   * the X-Organization-Id header is attached.)
   */
  exportRoadDashboardUrl: (format: 'md' | 'pdf') =>
    `/api/v1/observability/road-dashboard/export?format=${format}`,
};
